import throttle from 'lodash/throttle';
import { useCallback, useEffect, useRef } from 'react';
/**
 * Custom hook for tracking user engagement metrics including scroll progress,
 * time spent on page, and element visibility
 */
// eslint-disable-next-line max-lines-per-function
export const useEngagementTracking = function useEngagementTracking() {
  let {
    elementIds = [],
    enableScrollTracking = false,
    rootMargin = '0px',
    threshold = 0.3,
    enableTimeSpentTracking = false,
    scrollMilestones = [25, 50, 75, 100],
    timeSpentMilestones = [30, 60, 90, 120],
    onScrollProgress,
    onElementVisible,
    onTimeSpent
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const scrollProgressTracked = useRef(new Set());
  const elementVisibilityTracked = useRef(new Set());
  const timeSpentTracked = useRef(new Set());
  const startTimeRef = useRef(Date.now());
  const timeSpentIntervalRef = useRef(null);
  const observerRef = useRef(null);

  // Track scroll progress
  const handleScroll = useCallback(() => {
    if (!enableScrollTracking) return;
    const scrollTop = document.body.scrollTop;
    const docHeight = document.body.scrollHeight;
    const winHeight = document.body.clientHeight;
    const scrollPercent = Math.round(scrollTop / (docHeight - winHeight) * 100);

    // Track configurable scroll progress milestones
    scrollMilestones.forEach(milestone => {
      if (scrollPercent >= milestone && !scrollProgressTracked.current.has(milestone)) {
        scrollProgressTracked.current.add(milestone);
        onScrollProgress === null || onScrollProgress === void 0 ? void 0 : onScrollProgress(milestone, docHeight, winHeight);
      }
    });
  }, [enableScrollTracking, scrollMilestones, onScrollProgress]);

  // Track element visibility
  const handleIntersection = useCallback(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.target.id) {
        const elementId = entry.target.id;
        if (!elementVisibilityTracked.current.has(elementId)) {
          elementVisibilityTracked.current.add(elementId);
          onElementVisible === null || onElementVisible === void 0 ? void 0 : onElementVisible(elementId, entry.intersectionRatio);
        }
      }
    });
  }, [onElementVisible]);

  // Setup intersection observer
  useEffect(() => {
    if (elementIds.length === 0) return;
    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin,
      threshold
    });

    // Observe all specified elements
    elementIds.forEach(elementId => {
      const element = document.getElementById(elementId);
      if (element) {
        var _observerRef$current;
        (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 ? void 0 : _observerRef$current.observe(element);
      }
    });
    return () => {
      var _observerRef$current2;
      (_observerRef$current2 = observerRef.current) === null || _observerRef$current2 === void 0 ? void 0 : _observerRef$current2.disconnect();
    };
  }, [elementIds, handleIntersection, rootMargin, threshold]);

  // Setup scroll listener
  useEffect(() => {
    if (!enableScrollTracking) return;
    const throttledHandleScroll = throttle(handleScroll, 500);
    window.document.body.addEventListener('scroll', throttledHandleScroll, {
      passive: true
    });

    // Check initial scroll position
    handleScroll();
    return () => {
      window.document.body.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [handleScroll, enableScrollTracking]);

  // Setup time spent tracking
  useEffect(() => {
    if (!enableTimeSpentTracking) return;

    // Reset start time and tracking when effect runs
    startTimeRef.current = Date.now();
    timeSpentTracked.current.clear();

    // Check time spent every 5 seconds
    timeSpentIntervalRef.current = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);

      // Track configurable time spent milestones
      timeSpentMilestones.forEach(milestone => {
        if (elapsedSeconds >= milestone && !timeSpentTracked.current.has(milestone)) {
          timeSpentTracked.current.add(milestone);
          onTimeSpent === null || onTimeSpent === void 0 ? void 0 : onTimeSpent(milestone);
        }
      });

      // Clear interval if all milestones have been reached
      if (timeSpentTracked.current.size >= timeSpentMilestones.length) {
        if (timeSpentIntervalRef.current) {
          clearInterval(timeSpentIntervalRef.current);
          timeSpentIntervalRef.current = null;
        }
      }
    }, 5000); // Check every 5 seconds

    return () => {
      if (timeSpentIntervalRef.current) {
        clearInterval(timeSpentIntervalRef.current);
        timeSpentIntervalRef.current = null;
      }
    };
  }, [enableTimeSpentTracking, timeSpentMilestones, onTimeSpent]);
};
export default useEngagementTracking;